import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/** 'granted' = analytics/cookies allowed; 'denied' = essential only; null = no choice yet. */
export type ConsentState = 'granted' | 'denied' | null;
const KEY = 'mm_cookie_consent';

/**
 * Stores the visitor's cookie-consent decision in localStorage and exposes it
 * reactively. Any future analytics/marketing scripts should gate themselves on
 * `granted` (or subscribe to `consent$`) rather than loading unconditionally.
 */
@Injectable({ providedIn: 'root' })
export class ConsentService {
  private _state$ = new BehaviorSubject<ConsentState>(this.read());
  /** Emits the current consent state and again whenever it changes. */
  readonly consent$ = this._state$.asObservable();

  /** True once the visitor has made any choice (so the banner can hide). */
  get decided(): boolean { return this._state$.value !== null; }

  /** True only when the visitor explicitly accepted. */
  get granted(): boolean { return this._state$.value === 'granted'; }

  accept(): void { this.write('granted'); }
  reject(): void { this.write('denied'); }

  /** Clears the saved decision (e.g. a "manage cookies" link re-opening the banner). */
  reset(): void {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(KEY);
    this._state$.next(null);
  }

  private write(state: Exclude<ConsentState, null>): void {
    if (typeof localStorage !== 'undefined') localStorage.setItem(KEY, state);
    this._state$.next(state);
  }

  private read(): ConsentState {
    if (typeof localStorage === 'undefined') return null;
    const v = localStorage.getItem(KEY);
    return v === 'granted' || v === 'denied' ? v : null;
  }
}
